import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_SUGGESTION_PAGE_SIZE,
  filterLocalRows,
  getDisplayText,
  getSuggestionColumns,
  resolveRowId,
  resolveSearchColumnValue
} from '../helpers';
import type { SearchFieldColumn, SearchRequestFn } from '../types';

const DROPDOWN_MIN_WIDTH = 360;
const DROPDOWN_MAX_WIDTH = 720;
const DROPDOWN_MAX_HEIGHT = 420;

const areDropdownStylesEqual = (left: CSSProperties, right: CSSProperties) => {
  const leftKeys = Object.keys(left) as Array<keyof CSSProperties>;
  const rightKeys = Object.keys(right) as Array<keyof CSSProperties>;

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every(key => left[key] === right[key]);
};

interface UseSearchSuggestionsParams<T> {
  columnDisplay?: keyof T;
  displayRelatedField?: keyof T;
  onRowSelection?: (row: T | null) => void;
  onValueChange?: (value: any) => void;
  pageSize?: number;
  rows?: T[];
  searchColumns?: SearchFieldColumn<T>[];
  searchRequest: SearchRequestFn<T> | null;
  value?: any;
}

export const useSearchSuggestions = <T>({
  columnDisplay,
  displayRelatedField,
  onRowSelection,
  onValueChange,
  pageSize = DEFAULT_SUGGESTION_PAGE_SIZE,
  rows,
  searchColumns = [],
  searchRequest,
  value
}: UseSearchSuggestionsParams<T>) => {
  const [selectedObj, setSelectedObj] = useState<T | null>(null);
  const [inputValue, setInputValue] = useState(() => (value == null ? '' : String(value)));
  const [debouncedValue, setDebouncedValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [serverSuggestions, setServerSuggestions] = useState<T[]>([]);
  const [serverCurrentPage, setServerCurrentPage] = useState(0);
  const [serverTotalItems, setServerTotalItems] = useState(0);
  const [localCurrentPage, setLocalCurrentPage] = useState(0);
  const [dropdownPositionStyle, setDropdownPositionStyle] = useState<CSSProperties>({});
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const normalizedValueRef = useRef(value == null ? '' : String(value));
  const previousPropValueRef = useRef(value == null ? '' : String(value));
  const isUserTypingRef = useRef(false);
  const isCommittingSelectionRef = useRef(false);
  const requestIdRef = useRef(0);
  const suggestionContainerRef = useRef<HTMLDivElement | null>(null);
  const suggestionDropdownRef = useRef<HTMLDivElement | null>(null);
  const shouldUseServer = Boolean(searchRequest);
  const effectivePageSize = pageSize > 0 ? pageSize : DEFAULT_SUGGESTION_PAGE_SIZE;
  const updateDropdownPositionStyle = useCallback((nextStyle: CSSProperties) => {
    setDropdownPositionStyle(previousStyle =>
      areDropdownStylesEqual(previousStyle, nextStyle) ? previousStyle : nextStyle
    );
  }, []);

  const primaryField = useMemo(() => {
    if (columnDisplay && String(columnDisplay).trim()) {
      return String(columnDisplay);
    }

    const fallbackColumn = searchColumns.find(column => column.field?.trim() && !column.checkboxSelection);
    return fallbackColumn?.field;
  }, [columnDisplay, searchColumns]);

  const secondaryField = useMemo(() => {
    if (displayRelatedField && String(displayRelatedField).trim()) {
      return String(displayRelatedField);
    }

    const fallbackColumn = searchColumns.find(
      column => column.field?.trim() && !column.checkboxSelection && column.field !== primaryField
    );

    return fallbackColumn?.field;
  }, [displayRelatedField, primaryField, searchColumns]);

  const suggestionColumns = useMemo(
    () => getSuggestionColumns(searchColumns, primaryField, secondaryField),
    [primaryField, searchColumns, secondaryField]
  );

  const filteredLocalRows = useMemo(() => filterLocalRows(rows ?? [], debouncedValue), [debouncedValue, rows]);
  const paginatedLocalRows = useMemo(() => {
    const startIndex = localCurrentPage * effectivePageSize;
    return filteredLocalRows.slice(startIndex, startIndex + effectivePageSize);
  }, [effectivePageSize, filteredLocalRows, localCurrentPage]);

  const suggestions = shouldUseServer ? serverSuggestions : paginatedLocalRows;
  const totalItems = shouldUseServer ? serverTotalItems : filteredLocalRows.length;
  const currentPage = shouldUseServer ? serverCurrentPage : localCurrentPage;
  const shouldShowDropdown =
    isFocused &&
    debouncedValue.trim().length > 0 &&
    (isLoading || suggestions.length > 0 || shouldUseServer || filteredLocalRows.length > 0);
  const showNoResults = shouldShowDropdown && !isLoading && suggestions.length === 0;
  const canGoPrevious = currentPage > 0;
  const canGoNext = totalItems > (currentPage + 1) * effectivePageSize;
  const showPagination = totalItems > effectivePageSize;

  const getRowDisplayValue = useCallback(
    (row: T | null) => (row ? getDisplayText(row, primaryField) : ''),
    [primaryField]
  );

  const clearSelection = useCallback(() => {
    setSelectedObj(null);
  }, []);

  const commitSelection = useCallback(
    (row: T | null) => {
      isCommittingSelectionRef.current = true;

      if (!row) {
        normalizedValueRef.current = '';
        setSelectedObj(null);
        setInputValue('');
        setIsFocused(false);
        setHighlightedIndex(-1);
        onValueChange?.('');
        onRowSelection?.(null);
        window.setTimeout(() => {
          isCommittingSelectionRef.current = false;
        }, 0);
        return;
      }

      const displayValue = getRowDisplayValue(row);

      normalizedValueRef.current = displayValue;
      setSelectedObj(row);
      setInputValue(displayValue);
      setIsFocused(false);
      setHighlightedIndex(-1);
      onValueChange?.(displayValue);
      onRowSelection?.(row);

      window.setTimeout(() => {
        isCommittingSelectionRef.current = false;
      }, 0);
    },
    [getRowDisplayValue, onRowSelection, onValueChange]
  );

  const handleInputChange = useCallback(
    (nextValue: string) => {
      isUserTypingRef.current = true;
      normalizedValueRef.current = nextValue;
      setInputValue(nextValue);
      setIsFocused(true);

      if (selectedObj) {
        setSelectedObj(null);
      }

      onValueChange?.(nextValue);

      if (!nextValue) {
        onRowSelection?.(null);
      }

      window.setTimeout(() => {
        isUserTypingRef.current = false;
      }, 100);
    },
    [onRowSelection, onValueChange, selectedObj]
  );

  const handleInputFocus = useCallback(() => {
    setHasUserInteracted(true);
    if (shouldUseServer) {
      setServerCurrentPage(0);
    } else {
      setLocalCurrentPage(0);
    }
    setIsFocused(true);
  }, [shouldUseServer]);

  const handleInputBlur = useCallback(() => {
    window.setTimeout(() => {
      if (isCommittingSelectionRef.current) {
        return;
      }

      if (suggestionDropdownRef.current?.matches(':hover')) {
        setIsFocused(true);
        return;
      }

      isUserTypingRef.current = false;

      setIsFocused(false);
    }, 120);
  }, []);

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const isArrowKey = event.key === 'ArrowDown' || event.key === 'ArrowUp';

      if (isArrowKey && inputValue.trim()) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!shouldShowDropdown) {
        return;
      }

      if (event.key === 'ArrowDown') {
        if (suggestions.length === 0) return;
        setHighlightedIndex(previousIndex => (previousIndex < suggestions.length - 1 ? previousIndex + 1 : 0));
        return;
      }

      if (event.key === 'ArrowUp') {
        if (suggestions.length === 0) return;
        setHighlightedIndex(previousIndex => (previousIndex <= 0 ? suggestions.length - 1 : previousIndex - 1));
        return;
      }

      if (event.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        event.preventDefault();
        commitSelection(suggestions[highlightedIndex]);
        return;
      }

      if (event.key === 'Escape') {
        setIsFocused(false);
      }
    },
    [commitSelection, highlightedIndex, inputValue, shouldShowDropdown, suggestions]
  );

  const getColumnDisplayValue = useCallback((row: T, column: SearchFieldColumn<T>) => {
    const { value: resolvedValue } = resolveSearchColumnValue(row, column);
    return typeof resolvedValue === 'string' || typeof resolvedValue === 'number' ? String(resolvedValue) : '';
  }, []);

  const getColumnStyle = useCallback((column: SearchFieldColumn<T>, index: number) => {
    if (column.width) {
      return { width: `${column.width}px`, flex: '0 0 auto' } as CSSProperties;
    }

    if (column.flex) {
      return { flex: `${column.flex} ${column.flex} 0%`, minWidth: 0 } as CSSProperties;
    }

    if (index === 0) {
      return { flex: '0 0 100px', width: '100px' } as CSSProperties;
    }

    return { flex: '1 1 auto', minWidth: 0 } as CSSProperties;
  }, []);

  const goToPreviousPage = useCallback(() => {
    if (isLoading || !canGoPrevious) {
      return;
    }

    if (shouldUseServer) {
      setServerCurrentPage(previousPage => Math.max(0, previousPage - 1));
      return;
    }

    setLocalCurrentPage(previousPage => Math.max(0, previousPage - 1));
  }, [canGoPrevious, isLoading, shouldUseServer]);

  const goToNextPage = useCallback(() => {
    if (isLoading || !canGoNext) {
      return;
    }

    if (shouldUseServer) {
      setServerCurrentPage(previousPage => previousPage + 1);
      return;
    }

    setLocalCurrentPage(previousPage => previousPage + 1);
  }, [canGoNext, isLoading, shouldUseServer]);

  useEffect(() => {
    const nextValue = value == null ? '' : String(value);
    const previousPropValue = previousPropValueRef.current;

    if (nextValue === previousPropValue) {
      return;
    }

    previousPropValueRef.current = nextValue;

    if (isUserTypingRef.current) {
      return;
    }

    const nextSelectedObj = (rows ?? []).find(row => getRowDisplayValue(row) === nextValue) ?? null;
    const currentSelectedValue = getRowDisplayValue(selectedObj);
    const hasSelectedRowChanged =
      resolveRowId(selectedObj) !== resolveRowId(nextSelectedObj) || currentSelectedValue !== nextValue;

    if (normalizedValueRef.current !== nextValue) {
      normalizedValueRef.current = nextValue;
      setInputValue(nextValue);
    }

    if (!nextValue) {
      if (selectedObj) {
        setSelectedObj(null);
      }
      return;
    }

    if (nextSelectedObj) {
      if (hasSelectedRowChanged) {
        setSelectedObj(nextSelectedObj);
      }
      return;
    }

    if (selectedObj && currentSelectedValue !== nextValue) {
      setSelectedObj(null);
    }
  }, [getRowDisplayValue, rows, selectedObj, value]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(inputValue.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [inputValue]);

  useEffect(() => {
    if (isCommittingSelectionRef.current) {
      return;
    }

    if (!isFocused) {
      return;
    }

    if (shouldUseServer) {
      setServerCurrentPage(0);
      return;
    }

    setLocalCurrentPage(0);
  }, [debouncedValue, isFocused, shouldUseServer]);

  useEffect(() => {
    if (isCommittingSelectionRef.current) {
      return;
    }

    if (!shouldUseServer || !hasUserInteracted || !isFocused) {
      return;
    }

    if (!debouncedValue) {
      setServerSuggestions([]);
      setServerTotalItems(0);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);

    searchRequest?.({
      page: serverCurrentPage,
      pageSize: effectivePageSize,
      query: debouncedValue
    })
      .then(result => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setServerSuggestions(result.items);
        setServerTotalItems(result.total);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setServerSuggestions([]);
        setServerTotalItems(0);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
  }, [
    debouncedValue,
    effectivePageSize,
    hasUserInteracted,
    isFocused,
    searchRequest,
    serverCurrentPage,
    shouldUseServer
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;

      if (suggestionContainerRef.current?.contains(targetNode)) return;
      if (suggestionDropdownRef.current?.contains(targetNode)) return;

      setIsFocused(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!shouldShowDropdown) {
      setHighlightedIndex(-1);
      return;
    }

    setHighlightedIndex(suggestions.length > 0 ? 0 : -1);
  }, [shouldShowDropdown, suggestions]);

  useEffect(() => {
    if (typeof window === 'undefined' || !shouldShowDropdown) {
      updateDropdownPositionStyle({});
      return;
    }

    const containerRect = suggestionContainerRef.current?.getBoundingClientRect();

    if (!containerRect) {
      updateDropdownPositionStyle({});
      return;
    }

    const estimatedContentWidth = suggestionColumns.reduce((totalWidth, column, index) => {
      if (typeof column.width === 'number') {
        return totalWidth + column.width;
      }

      const sampleTextLength = suggestions.slice(0, 8).reduce(
        (maxLength, row) => {
          const nextLength = getColumnDisplayValue(row, column).length;
          return Math.max(maxLength, nextLength);
        },
        String(column.field ?? '').length
      );

      const fallbackWidth = index === 0 ? 100 : 260;
      const estimatedWidth = Math.max(fallbackWidth, Math.min(360, sampleTextLength * 8 + 36));
      return totalWidth + estimatedWidth;
    }, 0);

    const desiredWidth = Math.max(containerRect.width, DROPDOWN_MIN_WIDTH, estimatedContentWidth);
    const availableRightSpace = window.innerWidth - containerRect.left - 12;
    const resolvedWidth = Math.min(desiredWidth, DROPDOWN_MAX_WIDTH, availableRightSpace);
    const availableTopSpace = containerRect.top - 12;
    const availableBottomSpace = window.innerHeight - containerRect.bottom - 12;
    const shouldOpenAbove = availableBottomSpace < 220 && availableTopSpace > availableBottomSpace;
    const availableSpace = shouldOpenAbove ? availableTopSpace : availableBottomSpace;
    const resolvedMaxHeight = Math.max(160, Math.min(DROPDOWN_MAX_HEIGHT, availableSpace));

    const baseStyle = {
      left: containerRect.left,
      width: resolvedWidth,
      maxHeight: resolvedMaxHeight
    };

    if (shouldOpenAbove) {
      updateDropdownPositionStyle({
        ...baseStyle,
        top: containerRect.top,
        transform: 'translateY(-100%)',
        marginBottom: 4
      });
      return;
    }

    updateDropdownPositionStyle({
      ...baseStyle,
      top: containerRect.bottom,
      marginTop: 4
    });
  }, [getColumnDisplayValue, shouldShowDropdown, suggestionColumns, suggestions, updateDropdownPositionStyle]);

  return {
    canGoNext,
    canGoPrevious,
    clearSelection,
    commitSelection,
    currentPage,
    dropdownPositionStyle,
    getColumnDisplayValue,
    getColumnStyle,
    goToNextPage,
    goToPreviousPage,
    handleInputBlur,
    handleInputChange,
    handleInputFocus,
    handleInputKeyDown,
    highlightedIndex,
    inputValue,
    isLoading,
    selectedObj,
    setHighlightedIndex,
    setSelectedObj,
    shouldShowDropdown,
    showNoResults,
    showPagination,
    suggestionColumns,
    suggestionContainerRef,
    suggestionDropdownRef,
    suggestions
  };
};
